import { Features } from "@storytell/cp-client";
import type { StreamingChatId, StreamingOperationId } from "@storytell/streaming";
import { TbHelpCircle } from "solid-icons/tb";
import { Show, createMemo } from "solid-js";
import { getModelDisplayName } from "~/utils/modelMapping";
import { getModelVendor } from "~/utils/modelVendors";
import { useWire } from "~/wire";
import { OperationCreateAction } from "./internal/MockOperationCreateAction";

interface TurnCompleteRendererProps {
  block: { operationId: StreamingOperationId };
  chatId: StreamingChatId;
}

/**
 * Renders a "virtual" block that shows completion details for a conversation.
 * This is not a real block that is generated by the server.
 */
export const TurnCompleteRenderer = (props: TurnCompleteRendererProps) => {
  const wire = useWire();

  const formatDuration = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
  };

  const formatTime = (date?: Date) => {
    if (!date) return "";
    // Use the actual date passed in, not the current date
    return date.toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Find the message that contains this operation
  const getMessage = () => {
    const operation = parentOperation();
    if (!operation) return null;

    const messageId = operation.messageId;
    if (!messageId) return null;

    return wire.stream.message.get(messageId);
  };

  // Get the actual model used from the blocks
  const getModelFeature = () => {
    const message = getMessage();
    if (!message) return undefined;

    // Look through the operations in the message to find the actual model used
    for (const operationId of message.operations ?? []) {
      const operation = wire.stream.operation.get(operationId);
      if (!operation) continue;

      // Look through blocks in this operation
      for (const blockId of operation.blocks ?? []) {
        const block = wire.stream.block.get(blockId);

        // Return the first non-STORYTELL_AGENT_V3 feature (which represents the actual model picked)
        if (block?.feature && block.feature !== Features.Feature.MODEL_STORYTELL_AGENT_V3) {
          return block.feature;
        }
      }
    }

    // Fallback to message feature if no block feature found
    return message.feature;
  };

  // Get credits and token information from the specific message
  const getConversationMetrics = () => {
    const message = getMessage();
    const metrics = message?.aggregateMetrics;

    return {
      totalCredits: metrics?.totalCredits || 0,
      totalTokens: (metrics?.ingressTokens || 0) + (metrics?.egressTokens || 0),
      ingressTokens: metrics?.ingressTokens || 0,
      egressTokens: metrics?.egressTokens || 0,
    };
  };

  // Check if we have any metric data to show
  const hasMetrics = () => {
    const metrics = getConversationMetrics();
    return metrics.totalCredits > 0 || metrics.totalTokens > 0;
  };

  // Find the parent operation for this block to get completion timestamp
  const parentOperation = () => wire.stream.operation.get(props.block.operationId);

  // Get completion timestamp and processing time from the operation
  const operationCompletedAt = createMemo(() => {
    return parentOperation()?.completedAt;
  });

  const operationProcessingTime = createMemo(() => {
    const op = parentOperation();
    if (op?.completedAt && op.startedAt) {
      return op.completedAt.getTime() - op.startedAt.getTime();
    }
    return undefined;
  });

  return (
    <div class="pl-4 flex flex-col gap-2 min-w-0 w-full" data-renderer="conversation-complete">
      <div class="flex items-start justify-between gap-4">
        <div class="text-xs text-muted-text flex items-start gap-3 flex-wrap">
          <Show when={operationCompletedAt()}>
            <span>Completed {formatTime(operationCompletedAt())}</span>
          </Show>
          <Show when={operationProcessingTime()}>
            <span>{formatDuration(operationProcessingTime()!)}</span>
          </Show>
          {/* Model used */}
          <Show when={getModelFeature()}>
            {(() => {
              const modelFeature = getModelFeature();
              if (!modelFeature) return null;
              const vendor = getModelVendor(modelFeature);
              const VendorIcon = vendor.icon;

              return (
                <div class="flex items-center gap-1">
                  <VendorIcon size={14} class="text-muted-text" />
                  <span class="font-medium text-muted-text">{getModelDisplayName(modelFeature)}</span>
                </div>
              );
            })()}
          </Show>
          {/* Credit consumption */}
          <Show when={hasMetrics() && getConversationMetrics() && getConversationMetrics().totalCredits > 0}>
            <div class="flex items-center gap-2">
              <span>
                <span class="font-bold">{getConversationMetrics().totalCredits.toLocaleString("en-US")}</span> total
                credits used
              </span>
              <a
                class="inline text-brand text-xs"
                href="https://go.storytell.ai/learn-credits"
                target="_blank"
                rel="noreferrer"
              >
                <TbHelpCircle class="size-4" />
              </a>
            </div>
          </Show>
        </div>
        {/* Create Action button - moved inline and made smaller */}
        <Show when={hasMetrics()}>
          <div class="flex-shrink-0">
            <OperationCreateAction credits={getConversationMetrics().totalCredits} />
          </div>
        </Show>
      </div>
    </div>
  );
};
